/**
 * Activity Tracker Utility
 * Tracks user navigation history and API calls for incident reporting
 */

class ActivityTracker {
  constructor() {
    this.maxHistorySize = 10; // Store last 10 items
    this.navigationHistory = [];
    this.apiCallHistory = [];
  }

  // Track page navigation
  trackNavigation(page, timestamp = new Date()) {
    try {
      const navigationEntry = {
        page: page,
        timestamp: timestamp.toLocaleString(),
        timestampISO: timestamp.toISOString()
      };

      this.navigationHistory.push(navigationEntry);

      // Keep only the last N entries
      if (this.navigationHistory.length > this.maxHistorySize) {
        this.navigationHistory.shift();
      }
    } catch (error) {
      // Silently fail - don't break the app
      console.warn('ActivityTracker: Failed to track navigation', error);
    }
  }

  // Track API calls
  trackAPICall(method, url, status, timestamp = new Date()) {
    try {
      const apiEntry = {
        method: method,
        url: url,
        status: status,
        timestamp: timestamp.toLocaleString(),
        timestampISO: timestamp.toISOString()
      };

      this.apiCallHistory.push(apiEntry);

      // Keep only the last N entries
      if (this.apiCallHistory.length > this.maxHistorySize) {
        this.apiCallHistory.shift();
      }
    } catch (error) {
      // Silently fail - don't break the app
      console.warn('ActivityTracker: Failed to track API call', error);
    }
  }

  // Get navigation history
  getNavigationHistory() {
    return [...this.navigationHistory];
  }

  // Get API call history
  getAPICallHistory() {
    return [...this.apiCallHistory];
  }

  // Get formatted navigation history as string
  getFormattedNavigationHistory() {
    if (this.navigationHistory.length === 0) {
      return 'No navigation history available';
    }

    return this.navigationHistory
      .map((entry, index) => `${index + 1}. ${entry.page} - ${entry.timestamp}`)
      .join('\n');
  }

  // Get formatted API call history as string
  getFormattedAPICallHistory() {
    if (this.apiCallHistory.length === 0) {
      return 'No API calls recorded';
    }

    return this.apiCallHistory
      .map((entry, index) => 
        `${index + 1}. [${entry.method}] ${entry.url} - Status: ${entry.status} - ${entry.timestamp}`
      )
      .join('\n');
  }

  // Clear all history
  clearHistory() {
    this.navigationHistory = [];
    this.apiCallHistory = [];
  }

  // Get all activity as formatted text
  getFormattedActivity() {
    return {
      navigation: this.getFormattedNavigationHistory(),
      apiCalls: this.getFormattedAPICallHistory()
    };
  }
}

// Create singleton instance
const activityTracker = new ActivityTracker();

export default activityTracker;
